/**
 * Sometimes we need numbers which *appear* to be random, but need to be
 * reproducible so that they will be:
 *
 * 1. The same on each render, and…
 * 2. equivalent on both the server-side and client-side.
 *
 * In order to achieve that, we need to create a "seeded" random number
 * generator, and we need to ensure that the "seed" is equal on the server and
 * client-side. Fortunately, the `useId` React hook fulfills exactly this
 * purpose.
 *
 * Implementation is largely based upon this Stackoverflow answer:
 * https://stackoverflow.com/questions/521295/seeding-the-random-number-generator-in-javascript
 */

import { useId, useMemo } from "react";

/**
 * This is called a "hashing" function, which is like an intermediate seed, and
 * and is meant to produce very different outputs for two different input
 * strings even when the two input strings are very similar.
 */
function cyrb128(str: string) {
  let h1 = 1779033703,
    h2 = 3144134277,
    h3 = 1013904242,
    h4 = 2773480762;
  for (let i = 0, k; i < str.length; i++) {
    k = str.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  return [(h1 ^ h2 ^ h3 ^ h4) >>> 0, (h2 ^ h1) >>> 0, (h3 ^ h1) >>> 0, (h4 ^ h1) >>> 0];
}

function sfc32(a: number, b: number, c: number, d: number) {
  return function () {
    a >>>= 0;
    b >>>= 0;
    c >>>= 0;
    d >>>= 0;
    let t = (a + b) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    d = (d + 1) | 0;
    t = (t + d) | 0;
    c = (c + t) | 0;
    return (t >>> 0) / 4294967296;
  };
}

export const useReproducibleRandomNumberGenerator = () => {
  const id = useId();

  const seed = useMemo(() => cyrb128(id), [id]);

  /**
   * Deliberately *not* memoizing here, because we want to generate the same
   * random numbers on each subsequent render.
   */
  const reproducibleRandomNumberGenerator = sfc32(seed[0], seed[1], seed[2], seed[3]);

  return reproducibleRandomNumberGenerator;
};
